/**
 * Property link resolution + External Property Pages (#1714, toolkit
 * `external-property-pages`).
 *
 * The toolkit rewrites a mapped property's permalink with ONE `post_type_link`
 * filter, so every surface that reads get_permalink() follows. This is the
 * platform's version of that filter: every surface that builds a link to a
 * property's page asks `resolvePropertyLink` instead of filling the feed's PDP
 * route itself. The answer is the HomeRunner PDP route (exactly what each
 * surface computed before) unless the feed's
 * `additional_info.reverseProxy.externalPages` maps the property to its own
 * page.
 *
 * Settings live on the feed record every widget already holds, so this adds no
 * request. Everything is optional and absent = today's behaviour:
 *
 *   externalPages: {
 *     enabled?: boolean            // default false
 *     byProperty?: { [slug]: url } // per-property external page
 *     urlTemplate?: string         // absolute URL with [slug], for the rest
 *     carryStay?: boolean          // default true: dates/party ride along
 *   }
 *
 * URL safety mirrors Core::sanitize_url / is_absolute_http: absolute http(s)
 * only, anything else is ignored and the property keeps its HomeRunner link.
 * A URL that resolves to the feed's own PDP for the same slug is ignored too
 * (Core::is_loop_target): in the follow-up that redirects the HomeRunner PDP
 * to the external page, such a mapping would loop.
 *
 * Pure module: no React, no window. SSR and CSR must resolve identically (the
 * SSR-seeded feed record has no `feed_url`; see `isOwnPdpUrl`).
 */
import { trailingSlashModeOf, withTrailingSlash } from './trailing-slash'

/** The feed-level External Property Pages settings, as stored. */
export interface ExternalPagesSettings {
  enabled?: boolean
  byProperty?: Record<string, string>
  urlTemplate?: string
  carryStay?: boolean
}

/**
 * The slice of the feed record the resolver reads. Typed loosely on purpose:
 * widget-core's `Feed` types `reverseProxy` without `externalPages`, and the
 * value is validated at read time anyway (a hand-edited feed record can hold
 * anything).
 */
export interface PropertyHrefFeed {
  feed_url?: string | null
  additional_info?: { reverseProxy?: unknown; [key: string]: unknown } | null
}

export interface PropertyHrefInput {
  feed: PropertyHrefFeed | null | undefined
  slug: string
  /** The feed's PDP route pattern, e.g. `/property/[slug]` (callers resolve their own fallback). */
  pdpRoute: string
  /**
   * Percent-encode the slug into the PDP route. destination-search does
   * (`propertyHref`); every other surface always filled it in raw, and the
   * resolver keeps each surface's bytes.
   */
  encodeSlug?: boolean
}

export interface PropertyLink {
  href: string
  /** True when `href` is the property's external page rather than its HomeRunner page. */
  external: boolean
  /**
   * Whether the dated search params (checkin/checkout/party) ride along.
   * Always true for a HomeRunner link; the feed's `carryStay` for an external one.
   */
  carryStay: boolean
}

/** Cap on `byProperty` entries: the map rides the public feed record every widget fetches. */
export const EXTERNAL_PAGES_MAX_ENTRIES = 500

const NOT_A_URL_CHAR = /[\u0000- \u007F\\]/
const ABSOLUTE_HTTP_PREFIX = /^https?:\/\/[^/?#\s\\]/i

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Characters that can close an HTML attribute or open a tag. Map popups and
 * the all-pins cards build their markup as strings with the href interpolated
 * (`href="${url}"`), so a stored `https://x.co/a"onmouseover="alert(1)` (no
 * whitespace, so it passes every other check) would break out of the
 * attribute. They are percent-encoded, as esc_url_raw does: the destination
 * is the same URL.
 */
const HTML_ATTRIBUTE_BREAKERS = /["'<>`]/g

/**
 * Accept only an absolute http(s) URL with a host; anything else becomes ''.
 * Returns the trimmed input (HTML-significant characters percent-encoded) when
 * it passes, with no other re-serialising: what the operator typed is what the
 * guest gets.
 *
 * Stricter than a URL parser on purpose: the WHATWG parser forgives
 * `https:///lodge/` (extra slashes), a backslash host, tabs and newlines, and
 * would turn each into a different host than the one the operator typed (the
 * toolkit validates before AND after esc_url_raw for the same reason).
 */
export function sanitizeExternalUrl(value: unknown): string {
  if (typeof value !== 'string') return ''
  const v = value.trim()
  if (!v || NOT_A_URL_CHAR.test(v) || !ABSOLUTE_HTTP_PREFIX.test(v)) return ''
  try {
    const u = new URL(v)
    if ((u.protocol === 'http:' || u.protocol === 'https:') && u.hostname) {
      return v.replace(HTML_ATTRIBUTE_BREAKERS, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)
    }
  } catch {
    // unparseable: fall through
  }
  return ''
}

/** The `[slug]` placeholder, the only one supported (not every surface has the property id). */
const SLUG_PLACEHOLDER = /\[slug\]/g

/** A template is usable when it names the slug, never the id, and expands to a valid URL. */
export function isValidUrlTemplate(template: unknown): template is string {
  if (typeof template !== 'string') return false
  const t = template.trim()
  if (!t.includes('[slug]') || t.includes('[id]')) return false
  return sanitizeExternalUrl(t.replace(SLUG_PLACEHOLDER, 'sample-slug')) !== ''
}

/**
 * The raw `externalPages` value off a feed record, untyped and unvalidated:
 * `undefined` when the feed never set it. Its identity is the stored value's,
 * which is what `usePropertyHrefFeed` keys on.
 */
export function externalPagesOf(
  feed: { additional_info?: { reverseProxy?: unknown } | null } | null | undefined,
): unknown {
  const reverseProxy = feed?.additional_info?.reverseProxy
  return isRecord(reverseProxy) ? reverseProxy.externalPages : undefined
}

/** The enabled settings, or undefined when the feature is off or the record holds junk. */
function readExternalPages(feed: PropertyHrefFeed | null | undefined): ExternalPagesSettings | undefined {
  const raw = externalPagesOf(feed)
  if (!isRecord(raw) || raw.enabled !== true) return undefined
  return raw as ExternalPagesSettings
}

function externalUrlFor(settings: ExternalPagesSettings, slug: string): string {
  const map = settings.byProperty
  if (isRecord(map) && Object.prototype.hasOwnProperty.call(map, slug)) {
    const mapped = sanitizeExternalUrl(map[slug])
    if (mapped) return mapped
  }
  if (isValidUrlTemplate(settings.urlTemplate)) {
    return sanitizeExternalUrl(settings.urlTemplate.trim().replace(SLUG_PLACEHOLDER, () => encodeURIComponent(slug)))
  }
  return ''
}

/** Host for a same-page comparison: case and a leading `www.` ignored. Shared with the renderer's redirect guard (#1718). */
export function hostKey(url: URL): string {
  return url.host.toLowerCase().replace(/^www\./, '')
}

/** A path for a same-page comparison: trailing slashes ignored. */
export function trimSlashes(path: string): string {
  return path.replace(/\/+$/, '')
}

/**
 * `href` with `query` appended (`?` or `&` by what it already holds), ahead
 * of any `#fragment`. The one join for a stay query on a link and for the
 * guest's query on the PDP redirect (#1718).
 */
export function appendQuery(href: string, query: string): string {
  if (!query) return href
  const hashAt = href.indexOf('#')
  const base = hashAt === -1 ? href : href.slice(0, hashAt)
  const hash = hashAt === -1 ? '' : href.slice(hashAt)
  return `${base}${base.includes('?') ? '&' : '?'}${query}${hash}`
}

/**
 * Does `externalUrl` point at the feed's own PDP for this slug? Compares host
 * (www and scheme ignored), path (trailing slash ignored) and, when the PDP
 * route carries one, the query. The PDP origin is the route itself when it is
 * absolute, else `feed_url`. The SSR-seeded feed record has no `feed_url`
 * (renderer feed-projection), so there only an absolute route can match; the
 * dashboard also rejects a self-pointing URL at save time, where the feed is
 * whole. A miss here leaves an equivalent link (same destination), never a
 * broken one.
 */
export function isOwnPdpUrl(externalUrl: string, ownPdpHref: string, feedUrl: string | null | undefined): boolean {
  try {
    const target = new URL(externalUrl)
    let own: URL
    if (/^https?:\/\//i.test(ownPdpHref)) own = new URL(ownPdpHref)
    else if (feedUrl) own = new URL(ownPdpHref, feedUrl)
    else return false
    if (hostKey(target) !== hostKey(own)) return false
    if (trimSlashes(target.pathname) !== trimSlashes(own.pathname)) return false
    return own.search === '' || own.search === target.search
  } catch {
    return false
  }
}

/**
 * The link to a property's page: its external page when the feed maps one,
 * else the feed's PDP route with the slug filled in (the exact expression the
 * surfaces used before this module existed).
 */
export function resolvePropertyLink({ feed, slug, pdpRoute, encodeSlug = false }: PropertyHrefInput): PropertyLink {
  // #1848: the feed's trailing-slash preference decides the form, so a card
  // click lands on the worker's canonical URL in one hop.
  const pdpHref = withTrailingSlash(
    pdpRoute.replace('[slug]', encodeSlug ? encodeURIComponent(slug) : slug),
    trailingSlashModeOf(feed),
  )
  const settings = slug ? readExternalPages(feed) : undefined
  if (settings) {
    const url = externalUrlFor(settings, slug)
    if (url && !isOwnPdpUrl(url, pdpHref, feed?.feed_url)) {
      return { href: url, external: true, carryStay: settings.carryStay !== false }
    }
  }
  return { href: pdpHref, external: false, carryStay: true }
}

/** `resolvePropertyLink(...).href`, for surfaces that only need the string. */
export function resolvePropertyHref(input: PropertyHrefInput): string {
  return resolvePropertyLink(input).href
}

/** Whether a property's link leaves HomeRunner, so a surface can mark it (`rel="noopener"`). */
export function isExternalPropertyHref(input: PropertyHrefInput): boolean {
  return resolvePropertyLink(input).external
}

/**
 * Append a surface's stay query (`checkin=…&adults=2`) to a resolved link.
 *
 * A HomeRunner link joins exactly as every surface did before (`?` or `&` by
 * what the base holds). An external link carries the stay only when the feed
 * says so (the toolkit appends the dated params after the permalink,
 * untouched), and inserts it ahead of any `#fragment`.
 */
export function withStayParams(link: PropertyLink, query: string): string {
  if (!query) return link.href
  if (!link.external) return `${link.href}${link.href.includes('?') ? '&' : '?'}${query}`
  if (!link.carryStay) return link.href
  return appendQuery(link.href, query)
}

/**
 * `rel` for an anchor that may point off the HomeRunner site. A card that
 * already opens in a new tab keeps its `noopener noreferrer`.
 */
export function propertyLinkRel(link: Pick<PropertyLink, 'external'>, openInNewTab = false): string | undefined {
  if (openInNewTab) return 'noopener noreferrer'
  return link.external ? 'noopener' : undefined
}
